// This file is based on mercator.js the Vector-Tile-Google-Maps project, created by Jesús Barrio
interface LatLng {
  lat: () => number;
  lng: () => number;
}
interface LatLngLiteral {
  lat: number;
  lng: number;
}
interface Point {
  x: number;
  y: number;
}
interface TileLocation {
  x: number;
  y: number;
  z: number;
}
interface TileBounds {
  sw: google.maps.LatLngLiteral;
  ne: google.maps.LatLngLiteral;
}

// Convert a `LatLng` Google Maps object to a `Point` object.
const fromLatLngToPoint = (latLng: LatLng): Point => {
  const siny = Math.min(Math.max(Math.sin(latLng.lat() * (Math.PI / 180)), -0.9999), 0.9999);
  return {
    x: 128 + latLng.lng() * (256 / 360),
    y: 128 + 0.5 * Math.log((1 + siny) / (1 - siny)) * -(256 / (2 * Math.PI)),
  };
};

// Converts a `Point` object to a `LatLng` literal object.
const fromPointToLatLng = (point: Point): LatLngLiteral => {
  const lat = (2 * Math.atan(Math.exp((point.y - 128) / -(256 / (2 * Math.PI)))) - Math.PI / 2) / (Math.PI / 180);
  const lng = (point.x - 128) / (256 / 360);
  return {
    lat: lat,
    lng: lng,
  };
};

// Returns a `TileLocation` object given a `LatLng` Google Maps object and a specified `zoom`.
const getTileAtLatLng = (latLng: LatLng, zoom: number, tileSize: number = 256): TileLocation => {
  const t = Math.pow(2, zoom);
  const s = tileSize / t;
  const p = fromLatLngToPoint(latLng);
  return {
    x: Math.floor(p.x / s),
    y: Math.floor(p.y / s),
    z: Math.round(zoom),
  };
};

// Returns a `TileBounds` object containing the Northeast and Southwest edges of a `TileLocation`.
const getTileBounds = (tile: TileLocation, tileSize: number = 256): TileBounds => {
  tile = normalizeTile(tile);
  const t = Math.pow(2, tile.z);
  const s = (tileSize / t);
  const sw = {
    x: tile.x * s,
    y: (tile.y * s) + s,
  };
  const ne = {
    x: tile.x * s + s,
    y: (tile.y * s),
  };
  return {
    sw: fromPointToLatLng(sw),
    ne: fromPointToLatLng(ne),
  };
};

// Normalizes the `x` and `y` of a `TileLocation` object.
const normalizeTile = (tile: TileLocation): TileLocation => {
  const t = Math.pow(2, tile.z);
  tile.x = ((tile.x % t) + t) % t;
  tile.y = ((tile.y % t) + t) % t;
  return tile;
};

// Converts a `LatLng` Google Maps object from coordinates on a `map` to pixels on screen.
const fromLatLngToPixels = (map: google.maps.Map, latLng: google.maps.LatLng): Point => {
  const bounds = map.getBounds();
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const topRight = map.getProjection().fromLatLngToPoint(ne);
  const bottomLeft = map.getProjection().fromLatLngToPoint(sw);
  const scale = Math.pow(2, map.getZoom());
  const worldPoint = map.getProjection().fromLatLngToPoint(latLng);
  return {
    x: (worldPoint.x - bottomLeft.x) * scale,
    y: (worldPoint.y - topRight.y) * scale,
  };
};

export {
  getTileAtLatLng,
  getTileBounds,
  fromLatLngToPixels,
};
